/**
 * useSyncGuruTimezone — la zona horaria del guru se pone sola según dónde está.
 *
 * El guru escribe sus horas en la hora del lugar donde atiende (9:00 = 9:00
 * donde está). Para que cada cliente las vea bien convertidas, el sistema
 * necesita saber esa zona. La tomamos del dispositivo del guru:
 *   - Cada vez que entra a su panel desde una zona nueva (viajó, o es su
 *     primera visita), su perfil toma esa zona.
 *   - Sus horas NO cambian: el trigger de profiles mueve la disponibilidad a la
 *     nueva zona conservando lo que escribió.
 *   - Si el guru elige otra zona a mano en Disponibilidad, se respeta hasta que
 *     su dispositivo cambie de zona otra vez.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import { useUserRoles } from '@/lib/hooks/useUserRoles';
import { TIMEZONES, getBrowserTimezone, tzShortLabel } from '@/lib/timezone';

const SEEN_KEY = 'gurumba:guru_tz_seen';

export function useSyncGuruTimezone() {
  const user = useUser();
  const { data: roles } = useUserRoles();
  const isGuru = !!roles?.includes('guru');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user || !isGuru) return;
    const deviceTz = getBrowserTimezone();
    if (!deviceTz) return;
    let seen: string | null = null;
    try { seen = window.localStorage.getItem(`${SEEN_KEY}:${user.id}`); } catch { /* sin storage */ }
    if (seen === deviceTz) return;

    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('profiles').select('timezone').eq('id', user.id).maybeSingle();
      if (cancelled || !data) return;
      try { window.localStorage.setItem(`${SEEN_KEY}:${user.id}`, deviceTz); } catch { /* sin storage */ }
      if (data.timezone === deviceTz) return;
      const { error } = await supabase.from('profiles').update({ timezone: deviceTz }).eq('id', user.id);
      if (error || cancelled) return;
      queryClient.invalidateQueries({ queryKey: ['guru-tz', user.id] });
      queryClient.invalidateQueries({ queryKey: ['guru-availability'] });
      const label = TIMEZONES.find((t) => t.value === deviceTz)?.label ?? tzShortLabel(deviceTz);
      toast.success(`Tu zona horaria ahora es ${label}, según tu ubicación. Tus horarios siguen iguales.`);
    })();
    return () => { cancelled = true; };
  }, [user, isGuru, queryClient]);
}
